// moms-harness:anchor v2
// JIRA: LOGISPRD-14923
import { SkuAttributeParams, toQuery } from '../queries/useGetSkuAttributeList'

/** 목록이 보여 주는 것 — 조회 조건(`_seq` 포함)과 페이지(1부터). 둘이 곧 목록 query key 다 */
export interface SkuAttributeView {
  params: SkuAttributeParams
  page: number
}

export const EMPTY_VIEW: SkuAttributeView = { params: {}, page: 1 }

/**
 * 마지막으로 본 목록 — **화면 밖(모듈)에 둔다** (LOGISPRD-14923).
 *
 * 다른 메뉴 탭으로 가면 이 화면은 언마운트된다. 돌아오면 필터의 진입 자동조회가 [조회]와 같은 경로로 새 `_seq` 를
 * 붙여 key 가 바뀌고, 캐시가 있어도 처음부터 다시 받았다. 마지막 조건·`_seq`·페이지를 여기 남겨 두었다가 같은 조건의
 * 진입이면 그대로 되돌려 캐시를 쓴다.
 *
 * **되살리는 조건은 그 페이지 캐시가 아직 있는 것이다**(`restorableView` 의 `isCached`). 탭을 닫으면 메뉴 코드가 든
 * 목록 캐시가 지워지고(`MenuOnSessionWrap.remove`), 보관 시간이 지나도 사라진다 — 그때는 되살리지 않고 새로 조회한다.
 * 그래서 이 값을 따로 비우는 경로는 [초기화]뿐이다. 브라우저 새로고침이면 모듈과 캐시가 함께 사라진다.
 */
let lastView: SkuAttributeView | undefined

/**
 * 조회 순번. **이것도 모듈에 둔다** — 화면 안(ref)에 두면 재진입 때 0 부터 다시 세어, 재진입 뒤 첫 [조회]가 예전 조회와
 * 같은 `_seq` 를 받는다. 조건까지 같으면 key 가 같아 [조회]를 눌렀는데 캐시가 나온다.
 */
let searchSeq = 0

export const nextSearchSeq = (): number => {
  searchSeq += 1
  return searchSeq
}

export const getLastView = (): SkuAttributeView | undefined => lastView

export const saveLastView = (view: SkuAttributeView): void => {
  lastView = view
}

export const clearLastView = (): void => {
  lastView = undefined
}

/**
 * 두 조회 조건이 **서버에 같은 요청을 보내는가.** `_seq` 와 빈 값은 보지 않는다 — URL 에서 복원한 조건은 빈 필드가
 * `undefined` 로 들어 있고, 조회한 조건은 `_seq` 가 붙어 있어 객체째 비교하면 늘 다르다.
 * 비교 기준을 `toQuery` 로 두어 서버 파라미터가 늘면 여기도 따라온다.
 */
export const isSameCondition = (a: SkuAttributeParams, b: SkuAttributeParams): boolean => JSON.stringify(toQuery(a)) === JSON.stringify(toQuery(b))

/**
 * 진입 조건으로 되돌릴 마지막 목록. 조건이 같고 **그 페이지 캐시가 남아 있을 때만** 돌려준다 — 다른 조건(URL 로 다른
 * 화주를 열었을 때 등)이거나 캐시가 지워졌으면 새로 조회해야 한다.
 */
export const restorableView = (
  params: SkuAttributeParams | undefined,
  isCached: (view: SkuAttributeView) => boolean,
): SkuAttributeView | undefined => {
  const last = getLastView()
  return last && params && isSameCondition(last.params, params) && isCached(last) ? last : undefined
}

/** 테스트 전용 — 모듈 상태를 비운다 (`console/data/holds.ts` 의 `__…ForTest` 선례) */
export const __resetLastViewForTest = (): void => {
  lastView = undefined
  searchSeq = 0
}
